import {ReactNode, useState} from "react"
import {Box, Button, Chip, Sheet, Stack, Typography, useTheme} from "@mui/joy"
import {SxProps, Theme} from "@mui/joy/styles/types"
import {Activity, AlertCircle, Calendar, CheckCircle2, Shield, Target, TrendingUp, Zap} from "lucide-react"
import {useTranslation} from "react-i18next"
import {Event, EventStatusCode} from "../types/event"
import {activePalette, applyTransparency} from "../theme"

type Prediction = Event["predictions"][number]
type GoalScales = Prediction["total_goals"]
type Confidence = "HIGH" | "MEDIUM" | "LOW"

export type MatchAnalysisProps = {
    event: Event
    language?: "en" | "fr"
    /**
     * Crests of the two sides.
     *
     * The prediction api carries no logo, so they are handed over by the page, which has them from
     * the team search. Without one a side falls back on its initials.
     */
    homeLogo?: string
    awayLogo?: string
    sx?: SxProps
}

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace"
const DISPLAY = "'Orbitron', sans-serif"

/** The accent green the buttons and inputs are built on, it has no palette token of its own */
const PROJECT_GREEN = "#2DDF82"

/**
 * The design mock came with its own palette. Every colour is mapped onto a project token here so
 * the panel reads as part of the app rather than as a transplant.
 *
 */
const matchPalette = (theme: Theme) => {
    const palette = activePalette(theme)

    return {
        accent: palette.primary["700"],
        accentSoft: palette.primary["600"],
        highlight: palette.primary["800"],
        positive: PROJECT_GREEN,
        warning: palette.warning["400"],
        negative: palette.danger["400"],
        muted: palette.neutral["400"],
        foreground: palette.primary["900"],
        bodyText: palette.text.secondary,
        /** Dark enough to read against an accent filled chip */
        backdrop: palette.primary["50"],
        surface: "rgba(255,255,255,0.05)",
        border: "rgba(255,255,255,0.1)",
        /** Sides keep a fixed colour across matches so the meters read the same way every time */
        home: PROJECT_GREEN,
        away: palette.primary["700"]
    }
}

type MatchPalette = ReturnType<typeof matchPalette>

const usePalette = (): MatchPalette => matchPalette(useTheme())

/** Palette tokens are hex today, the fallback keeps an unexpected format from breaking the panel */
const alpha = (color: string, opacity: number) => {
    try {
        return applyTransparency(color, opacity)
    } catch (e) {
        return color
    }
}

const panelSx = (colors: MatchPalette) => ({
    borderRadius: 15,
    padding: 2,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`
})

/** Status of the fixture, as a translation key and the colour it is read in */
const statusMeta = (colors: MatchPalette): { [code in EventStatusCode]: { labelKey: string, color: string } } => ({
    NOT_STARTED: {labelKey: "panelStatusScheduled", color: colors.muted},
    LIVE: {labelKey: "panelStatusLive", color: colors.negative},
    FINISHED: {labelKey: "panelStatusFinished", color: colors.positive},
    CANCELED: {labelKey: "panelStatusCanceled", color: colors.negative},
    DEFAULT: {labelKey: "panelStatusTbd", color: colors.muted}
})

const GOAL_LINES: { label: string, over: keyof GoalScales, under: keyof GoalScales }[] = [
    {label: "+0.5 Goals", over: "over_0_5", under: "under_0_5"},
    {label: "+1.5 Goals", over: "over_1_5", under: "under_1_5"},
    {label: "+2.5 Goals", over: "over_2_5", under: "under_2_5"},
    {label: "+3.5 Goals", over: "over_3_5", under: "under_3_5"},
]

/** Market a recommended bet belongs to, named by the key it is written with */
const BET_GROUP_KEYS: { [group: string]: string } = {
    match_result: "betMatchWinner",
    total_goals: "betTotalGoals",
    home_team_goals: "betHomeGoals",
    away_team_goals: "betAwayGoals",
    both_teams_score: "betBothTeamsScore",
    first_half_winner: "betFirstHalfWinner",
    team_to_score_first: "betFirstToScore",
    exact_score: "betExactScore",
}

/** Odds entries are provider specific — match the usual 1X2 keys, fall back on the first market. */
const MATCH_WINNER_KEYS = ["match_winner", "match_result", "1x2", "full_time_result", "winner"]

const toSnakeCase = (value: string) => value.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase()

const shortName = (name: string) => {
    const words = name.replace(/[^\p{L}\p{N} ]/gu, "").split(" ").filter(Boolean)
    if (words.length >= 3) return words.map(word => word[0]).join("").slice(0, 3).toUpperCase()
    return name.replace(/[^\p{L}\p{N}]/gu, "").slice(0, 3).toUpperCase()
}

const initials = (name: string) => {
    const words = name.split(" ").filter(Boolean)
    return (words.length > 1 ? words[0][0] + words[1][0] : name.slice(0, 2)).toUpperCase()
}

const latestPrediction = (event: Event): Prediction | undefined =>
    [...(event.predictions || [])]
        .sort((a, b) => new Date(b.run_at).getTime() - new Date(a.run_at).getTime())[0]

const matchWinnerOdds = (event: Event) =>
    (event.odds || []).find(odd => MATCH_WINNER_KEYS.includes(toSnakeCase(odd.key || ""))
        || MATCH_WINNER_KEYS.includes(toSnakeCase(odd.type || ""))) || (event.odds || [])[0]

const formatOdd = (odd?: number) => (typeof odd === "number" ? odd.toFixed(2) : "—")

const formatDateTime = (value: string) => {
    const date = new Date(value)
    if (isNaN(date.getTime())) return value
    return date.toLocaleString(undefined, {
        weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    })
}

/**
 * Exact score keys arrive as "2_1". Keys made of two bare digits ("21") are also accepted because
 * unquoted numeric keys collapse the separator away.
 */
const formatExactScore = (key: string) => {
    const separated = key.match(/^(\d+)_(\d+)$/)
    if (separated) return `${separated[1]}-${separated[2]}`
    if (/^\d{2}$/.test(key)) return `${key[0]}-${key[1]}`
    return key
}

const confidenceOf = (probability?: number): Confidence => {
    if (probability >= 65) return "HIGH"
    if (probability >= 45) return "MEDIUM"
    return "LOW"
}

const confidenceColor = (confidence: Confidence, colors: MatchPalette) =>
    confidence === "HIGH" ? colors.positive : confidence === "MEDIUM" ? colors.warning : colors.negative

const outcomeLabel = (outcome: string, event: Event, t: (key: string) => string) => {
    if (outcome === "home") return event.team_home.name
    if (outcome === "away") return event.team_away.name
    if (outcome === "draw") return t("panelDraw")
    if (outcome === "yes") return t("panelYes")
    if (outcome === "no") return t("panelNo")
    if (outcome === "neither") return t("panelNeither")
    const overUnder = outcome.match(/^(over|under)_(\d+)_(\d+)$/)
    if (overUnder) {
        const direction = overUnder[1] === "over" ? "Over" : "Under"
        return `${direction} ${overUnder[2]}.${overUnder[3]}`
    }
    if (/^\d+_\d+$/.test(outcome) || /^\d{2}$/.test(outcome)) return formatExactScore(outcome)
    return outcome.replace(/_/g, " ")
}

/**
 * Recommended bets are dotted paths into the prediction ("both_teams_score.yes"). Win bets may be
 * expressed on the match result itself ("match_result.home"), which is where the 1X2 odds come from.
 */
const resolveBet = (path: string, prediction: Prediction, event: Event, t: (key: string) => string) => {
    const [rawGroup, ...rest] = (path || "").split(".")
    const group = toSnakeCase(rawGroup || "")
    let outcome = toSnakeCase(rest.join("_"))

    const isWinMarket = group === "match_result" || group === "first_half_winner"
    if (isWinMarket) {
        if (outcome.includes("home")) outcome = "home"
        else if (outcome.includes("away")) outcome = "away"
        else if (outcome.includes("draw")) outcome = "draw"
    }

    const values: { [outcome: string]: number } = prediction[group as keyof Prediction] as any
    const probability = values && typeof values[outcome] === "number" ? values[outcome] : undefined

    const winnerOdds = matchWinnerOdds(event)
    const odds = group === "match_result" && winnerOdds ? {
        home: winnerOdds.values?.Home,
        draw: winnerOdds.values?.Draw,
        away: winnerOdds.values?.Away,
    }[outcome] : undefined

    let label = BET_GROUP_KEYS[group] ? t(BET_GROUP_KEYS[group]) : (group || "").replace(/_/g, " ")
    if (group === "home_team_goals") label = `${shortName(event.team_home.name)} ${t("betGoalsSuffix")}`
    if (group === "away_team_goals") label = `${shortName(event.team_away.name)} ${t("betGoalsSuffix")}`

    return {label, value: outcomeLabel(outcome, event, t), probability, odds}
}

const ArcGauge = (props: { value: number, color: string, size?: number, strokeWidth?: number }) => {
    const size = props.size || 80
    const strokeWidth = props.strokeWidth || 7
    const radius = (size - strokeWidth) / 2
    const center = size / 2
    const startAngle = -220
    const totalArc = 260
    const valueArc = (Math.min(Math.max(props.value, 0), 100) / 100) * totalArc
    const toRad = (degrees: number) => (degrees * Math.PI) / 180
    const arcPath = (from: number, to: number) => {
        const start = {x: center + radius * Math.cos(toRad(from)), y: center + radius * Math.sin(toRad(from))}
        const end = {x: center + radius * Math.cos(toRad(to)), y: center + radius * Math.sin(toRad(to))}
        const large = to - from > 180 ? 1 : 0
        return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${large} 1 ${end.x} ${end.y}`
    }
    return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path d={arcPath(startAngle, startAngle + totalArc)} fill={"none"} strokeLinecap={"round"}
              stroke={"rgba(255,255,255,0.06)"} strokeWidth={strokeWidth}/>
        {props.value > 0 && <path d={arcPath(startAngle, startAngle + valueArc)} fill={"none"} strokeLinecap={"round"}
                                  stroke={props.color} strokeWidth={strokeWidth}
                                  style={{filter: `drop-shadow(0 0 5px ${alpha(props.color, 0.5)})`}}/>}
    </svg>
}

const OverUnderBar = (props: { over: number, under: number, label: string }) => {
    const colors = usePalette()

    const over = props.over || 0
    const under = props.under != null ? props.under : 100 - over
    return <Stack gap={0.5}>
        <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography level={"body-xs"} sx={{fontFamily: MONO, color: colors.muted}}>{props.label}</Typography>
            <Typography level={"body-xs"} sx={{fontFamily: MONO, color: over >= 50 ? colors.positive : colors.negative}}>
                {over >= 50 ? `O ${over}%` : `U ${under}%`}
            </Typography>
        </Stack>
        <Stack direction={"row"} sx={{height: 6, borderRadius: "999px", overflow: "hidden"}}>
            <Box sx={{width: `${over}%`, background: colors.positive, opacity: 0.7}}/>
            <Box sx={{width: `${under}%`, background: colors.negative, opacity: 0.7}}/>
        </Stack>
    </Stack>
}

const ProgressRow = (props: { value: number, color: string, label: string }) => {
    const colors = usePalette()

    const value = props.value || 0
    return <Stack direction={"row"} alignItems={"center"} gap={1}>
        <Typography level={"body-xs"} noWrap
                    sx={{fontFamily: MONO, color: colors.muted, width: 64, textAlign: "right", flexShrink: 0}}>
            {props.label}
        </Typography>
        <Box sx={{flex: 1, height: 6, borderRadius: "999px", background: "rgba(255,255,255,0.06)"}}>
            <Box sx={{
                height: "100%",
                width: `${Math.min(value, 100)}%`,
                borderRadius: "999px",
                background: `linear-gradient(90deg, ${alpha(props.color, 0.53)}, ${props.color})`,
                boxShadow: `0 0 6px ${alpha(props.color, 0.31)}`,
            }}/>
        </Box>
        <Typography level={"body-xs"}
                    sx={{fontFamily: MONO, color: props.color, width: 32, textAlign: "right", flexShrink: 0}}>
            {Math.round(value)}%
        </Typography>
    </Stack>
}

const Pill = (props: { color: string, children: ReactNode }) => {
    return <Chip variant={"outlined"} size={"sm"} sx={{
        "--Chip-radius": "6px",
        fontFamily: MONO,
        fontSize: "0.65rem",
        letterSpacing: "0.05em",
        color: props.color,
        background: alpha(props.color, 0.09),
        borderColor: alpha(props.color, 0.27),
    }}>
        {props.children}
    </Chip>
}

const PanelHeader = (props: { icon: ReactNode, title: string, color: string, action?: ReactNode }) => {
    return <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} mb={1.5} gap={1}
                  flexWrap={"wrap"}>
        <Stack direction={"row"} alignItems={"center"} gap={0.75}>
            {props.icon}
            <Typography level={"body-xs"} sx={{
                fontFamily: MONO,
                fontSize: "0.625rem",
                fontWeight: 600,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: props.color,
            }}>
                {props.title}
            </Typography>
        </Stack>
        {props.action}
    </Stack>
}

const TeamColumn = (props: { name: string, color: string, alignment: string, logo?: string }) => {
    const colors = usePalette()
    // A crest that fails to load leaves the circle empty, the initials take over
    const [logoBroken, setLogoBroken] = useState(false)
    const showLogo = !!props.logo && !logoBroken

    return <Stack alignItems={"center"} gap={1}>
        <Box sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: alpha(props.color, 0.15),
            border: `1px solid ${alpha(props.color, 0.4)}`,
            boxShadow: `0 0 18px ${alpha(props.color, 0.16)}`,
        }}>
            {showLogo
                ? <Box component={"img"}
                       src={props.logo}
                       alt={props.name}
                       onError={() => setLogoBroken(true)}
                       sx={{width: 34, height: 34, objectFit: "contain"}}/>
                : <Typography sx={{fontFamily: DISPLAY, fontWeight: 700, fontSize: "1rem", color: props.color}}>
                    {initials(props.name)}
                </Typography>}
        </Box>
        <Stack alignItems={"center"}>
            <Typography textAlign={"center"}
                        sx={{fontFamily: DISPLAY, fontWeight: 700, fontSize: "0.85rem", color: colors.accent, overflowWrap: "anywhere"}}>
                {props.name}
            </Typography>
            <Typography level={"body-xs"} sx={{fontFamily: MONO, color: colors.muted}}>{props.alignment}</Typography>
        </Stack>
    </Stack>
}

const BetChip = (props: { label: string, value: string, probability?: number, odds?: number }) => {
    const colors = usePalette()

    const confidence = confidenceOf(props.probability)
    const color = confidenceColor(confidence, colors)
    return <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1.5}
                  sx={{
                      paddingX: 1.5,
                      paddingY: 1.25,
                      borderRadius: "8px",
                      background: colors.surface,
                      border: `1px solid ${alpha(colors.accent, 0.1)}`,
                      transition: "transform 200ms",
                      ":hover": {transform: "translateX(2px)"},
                  }}>
        <Stack direction={"row"} alignItems={"center"} gap={1} minWidth={0}>
            <Box sx={{
                width: 6, height: 6, flexShrink: 0, borderRadius: "50%",
                background: color, boxShadow: `0 0 4px ${color}`,
            }}/>
            <Stack minWidth={0}>
                <Typography level={"body-xs"} noWrap sx={{fontFamily: MONO, color: colors.muted}}>{props.label}</Typography>
                <Typography level={"body-xs"} noWrap
                            sx={{fontFamily: DISPLAY, fontSize: "0.65rem", fontWeight: 600, color: colors.accent}}>
                    {props.value}
                </Typography>
            </Stack>
        </Stack>
        <Stack alignItems={"flex-end"} gap={0.25} flexShrink={0}>
            <Typography level={"body-xs"} sx={{
                fontFamily: MONO,
                fontSize: "0.6rem",
                paddingX: 0.75,
                borderRadius: "4px",
                color,
                background: alpha(color, 0.08),
                border: `1px solid ${alpha(color, 0.2)}`,
            }}>
                {props.probability != null ? `${Math.round(props.probability)}%` : confidence}
            </Typography>
            <Typography level={"body-xs"} sx={{fontFamily: MONO, fontWeight: 700, color: colors.warning}}>
                {formatOdd(props.odds)}
            </Typography>
        </Stack>
    </Stack>
}

const MatchAnalysis = (props: MatchAnalysisProps) => {
    const {t, i18n} = useTranslation()
    // The reasoning comes written in both languages: the one being read wins
    const {event, language = (i18n.language === "en" ? "en" : "fr"), homeLogo, awayLogo} = props
    const [goalScope, setGoalScope] = useState<"total" | "home" | "away">("total")

    const prediction = latestPrediction(event)
    const colors = usePalette()
    const homeShort = shortName(event.team_home.name)
    const awayShort = shortName(event.team_away.name)
    const status = statusMeta(colors)[event.status_code] || statusMeta(colors).DEFAULT
    const showScore = event.status_code === "LIVE" || event.status_code === "FINISHED"
    const winnerOdds = matchWinnerOdds(event)

    const matchResult = prediction?.match_result
    const topProbability = matchResult
        ? Math.max(matchResult.home, matchResult.draw, matchResult.away)
        : undefined
    const modelConfidence = confidenceOf(topProbability)

    const goalScales: GoalScales = prediction && (goalScope === "total" ? prediction.total_goals
        : goalScope === "home" ? prediction.home_team_goals : prediction.away_team_goals)

    const exactScores = prediction ? Object.keys(prediction.exact_score)
        .filter(key => key !== "other")
        .map(key => ({score: formatExactScore(key), probability: prediction.exact_score[key] as number}))
        .sort((a, b) => b.probability - a.probability)
        .slice(0, 8) : []
    const maxExactScore = exactScores.length ? exactScores[0].probability : 1

    const recommendedBets = prediction ? Object.keys(prediction.recommended_bets)
        .sort()
        .map(rank => resolveBet(prediction.recommended_bets[rank] as string, prediction, event, t)) : []

    // No background of its own: the panel sits on the app surface, the grid only textures it
    return <Stack gap={{xs: 2, sm: 2.5}} sx={[{
        padding: {xs: 0, sm: 2},
        borderRadius: 15,
        backgroundImage: `linear-gradient(${alpha(colors.accent, 0.03)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(colors.accent, 0.03)} 1px, transparent 1px)`,
        backgroundSize: "48px 48px",
    }, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}>

        {/* Match hero */}
        <Sheet sx={{...panelSx(colors), padding: 0, overflow: "hidden", borderColor: alpha(colors.accent, 0.15)}}>
            <Stack paddingX={{xs: 2, sm: 3}} paddingTop={2.5} gap={2}>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1}
                       flexWrap={"wrap"}>
                    <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"}>
                        <Pill color={colors.accent}>{event.league.name}</Pill>
                        {event.round && <Typography level={"body-xs"} sx={{fontFamily: MONO, color: colors.muted}}>
                            {event.round}
                        </Typography>}
                    </Stack>
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                        <Pill color={status.color}>{t(status.labelKey)}</Pill>
                        {matchResult && <Pill color={confidenceColor(modelConfidence, colors)}>
                            {t(`panelConfidence${modelConfidence.charAt(0)}${modelConfidence.slice(1).toLowerCase()}`)}
                        </Pill>}
                    </Stack>
                </Stack>

                <Box display={"grid"} alignItems={"center"} gap={2}
                     gridTemplateColumns={{xs: "minmax(0, 1fr) minmax(0, 1fr)", sm: "minmax(0, 1fr) auto minmax(0, 1fr)"}}
                     gridTemplateAreas={{xs: `"home away" "middle middle"`, sm: `"home middle away"`}}>
                    <Box gridArea={"home"} minWidth={0}>
                        <TeamColumn name={event.team_home.name} color={colors.home} alignment={t("panelHome")} logo={homeLogo}/>
                    </Box>

                    {/* Middle column */}
                    <Stack gridArea={"middle"} alignItems={"center"} gap={1} paddingX={{xs: 0, sm: 2}}>
                        {event.referee && <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                            <Shield size={10} color={colors.muted}/>
                            <Typography level={"body-xs"} sx={{fontFamily: MONO, color: colors.muted}}>
                                {event.referee}
                            </Typography>
                        </Stack>}

                        {showScore ?
                            <Typography sx={{fontFamily: DISPLAY, fontWeight: 900, fontSize: "1.75rem", color: colors.accent}}>
                                {event.score?.final?.home ?? 0} - {event.score?.final?.away ?? 0}
                            </Typography> :
                            <Typography sx={{fontFamily: DISPLAY, fontWeight: 900, fontSize: "1.5rem", color: alpha(colors.muted, 0.5)}}>
                                VS
                            </Typography>}

                        <Box display={"grid"} gridTemplateColumns={"repeat(3, 1fr)"} gap={0.5} textAlign={"center"}>
                            {[
                                {label: t("panelHome"), code: homeShort, color: colors.home, odd: winnerOdds?.values?.Home},
                                {label: t("panelDraw"), code: "X", color: colors.warning, odd: winnerOdds?.values?.Draw},
                                {label: t("panelAway"), code: awayShort, color: colors.away, odd: winnerOdds?.values?.Away},
                            ].map(cell => <Stack key={cell.label} paddingY={0.75} paddingX={1} borderRadius={"4px"}
                                                 sx={{background: alpha(cell.color, 0.09)}}>
                                <Typography sx={{fontFamily: MONO, fontSize: "0.55rem", color: colors.muted}}>
                                    {cell.label}
                                </Typography>
                                <Typography level={"body-xs"} sx={{fontFamily: MONO, fontWeight: 700, color: cell.color}}>
                                    {cell.code}
                                </Typography>
                                <Typography level={"body-xs"} sx={{fontFamily: MONO, color: colors.warning}}>
                                    {formatOdd(cell.odd)}
                                </Typography>
                            </Stack>)}
                        </Box>

                        <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                            <Calendar size={10} color={colors.muted}/>
                            <Typography level={"body-xs"} sx={{fontFamily: MONO, color: colors.muted}}>
                                {formatDateTime(event.start_at)}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Box gridArea={"away"} minWidth={0}>
                        <TeamColumn name={event.team_away.name} color={colors.away} alignment={t("panelAway")} logo={awayLogo}/>
                    </Box>
                </Box>
            </Stack>

            {/* Win probability arcs */}
            {matchResult && <Box display={"grid"} gridTemplateColumns={"repeat(3, 1fr)"} marginTop={2.5}
                                 sx={{borderTop: `1px solid ${alpha(colors.accent, 0.08)}`}}>
                {[
                    {label: `${homeShort} ${t("panelWin")}`, value: matchResult.home, color: colors.home},
                    {label: t("panelDraw"), value: matchResult.draw, color: colors.warning},
                    {label: `${awayShort} ${t("panelWin")}`, value: matchResult.away, color: colors.away},
                ].map((item, index) => <Stack key={item.label} alignItems={"center"} paddingY={2} gap={0.5}
                                              sx={{borderRight: index < 2 ? `1px solid ${alpha(colors.accent, 0.08)}` : "none"}}>
                    <Box position={"relative"}>
                        <ArcGauge value={item.value} color={item.color}/>
                        <Stack position={"absolute"} top={0} bottom={0} left={0} right={0} marginTop={1}
                               alignItems={"center"} justifyContent={"center"}>
                            <Typography sx={{fontFamily: DISPLAY, fontWeight: 700, fontSize: {xs: "0.85rem", sm: "1rem"}, color: item.color}}>
                                {Math.round(item.value)}%
                            </Typography>
                        </Stack>
                    </Box>
                    <Typography textAlign={"center"}
                                sx={{fontFamily: MONO, fontSize: "0.625rem", letterSpacing: {xs: "0.05em", sm: "0.15em"}, color: colors.muted}}>
                        {item.label}
                    </Typography>
                </Stack>)}
            </Box>}
        </Sheet>

        {!prediction && <Sheet sx={panelSx(colors)}>
            <Stack alignItems={"center"} gap={1} paddingY={3}>
                <Zap size={16} color={colors.muted}/>
                <Typography level={"body-sm"} sx={{fontFamily: MONO, color: colors.muted}}>
                    No AI prediction available for this match yet
                </Typography>
            </Stack>
        </Sheet>}

        {prediction && <>
            {/* Metrics row */}
            <Box display={"grid"} gap={2} gridTemplateColumns={{xs: "1fr", md: "repeat(3, 1fr)"}}>
                <Sheet sx={panelSx(colors)}>
                    <PanelHeader icon={<Target size={11} color={colors.positive}/>} title={t("panelBtts")} color={colors.positive}/>
                    <Stack gap={0.75}>
                        <ProgressRow label={"YES"} value={prediction.both_teams_score.yes} color={colors.positive}/>
                        <ProgressRow label={"NO"} value={prediction.both_teams_score.no} color={colors.negative}/>
                    </Stack>
                    <Stack alignItems={"center"} marginTop={1.5} paddingTop={1}
                           sx={{borderTop: `1px solid ${alpha(colors.accent, 0.08)}`}}>
                        <Pill color={prediction.both_teams_score.yes >= 55 ? colors.positive : colors.negative}>
                            {prediction.both_teams_score.yes >= 55 ? "LIKELY YES" : "UNLIKELY"}
                        </Pill>
                    </Stack>
                </Sheet>

                <Sheet sx={panelSx(colors)}>
                    <PanelHeader icon={<Activity size={11} color={colors.warning}/>} title={t("panelFirstHalf")} color={colors.warning}/>
                    <Stack gap={0.75}>
                        <ProgressRow label={homeShort} value={prediction.first_half_winner.home} color={colors.home}/>
                        <ProgressRow label={"Draw"} value={prediction.first_half_winner.draw} color={colors.warning}/>
                        <ProgressRow label={awayShort} value={prediction.first_half_winner.away} color={colors.away}/>
                    </Stack>
                </Sheet>

                <Sheet sx={panelSx(colors)}>
                    <PanelHeader icon={<Zap size={11} color={colors.highlight}/>} title={t("panelFirstScorer")} color={colors.highlight}/>
                    <Stack gap={0.75}>
                        <ProgressRow label={homeShort} value={prediction.team_to_score_first.home} color={colors.home}/>
                        <ProgressRow label={"None"} value={prediction.team_to_score_first.neither} color={colors.muted}/>
                        <ProgressRow label={awayShort} value={prediction.team_to_score_first.away} color={colors.away}/>
                    </Stack>
                </Sheet>
            </Box>

            {/* Goals + exact scores */}
            <Box display={"grid"} gap={2} gridTemplateColumns={{xs: "1fr", md: "1fr 1fr"}}>
                <Sheet sx={panelSx(colors)}>
                    <PanelHeader icon={<TrendingUp size={11} color={colors.accent}/>} title={t("panelGoals")} color={colors.accent}
                                 action={<Stack direction={"row"} gap={0.5}>
                                     {([
                                         {scope: "total", label: "Tot"},
                                         {scope: "home", label: homeShort},
                                         {scope: "away", label: awayShort},
                                     ] as const).map(tab => {
                                         const active = goalScope === tab.scope
                                         return <Button key={tab.scope} variant={"plain"} size={"sm"}
                                                        onClick={() => setGoalScope(tab.scope)}
                                                        sx={{
                                                            minHeight: 0,
                                                            paddingX: 1,
                                                            paddingY: 0.25,
                                                            borderRadius: "4px",
                                                            fontFamily: MONO,
                                                            fontSize: "0.625rem",
                                                            fontWeight: 400,
                                                            color: active ? colors.accent : colors.muted,
                                                            background: active ? alpha(colors.accent, 0.13) : "transparent",
                                                            border: `1px solid ${alpha(colors.accent, active ? 0.27 : 0.08)}`,
                                                            ":hover": {background: alpha(colors.accent, 0.09), color: colors.accent},
                                                        }}>
                                             {tab.label}
                                         </Button>
                                     })}
                                 </Stack>}/>
                    <Stack gap={1.25}>
                        {GOAL_LINES.map(line => <OverUnderBar key={line.label} label={line.label}
                                                              over={goalScales[line.over]}
                                                              under={goalScales[line.under]}/>)}
                    </Stack>
                </Sheet>

                <Sheet sx={panelSx(colors)}>
                    <PanelHeader icon={<Target size={11} color={colors.highlight}/>} title={t("panelExactScore")} color={colors.highlight}/>
                    <Box display={"grid"} gridTemplateColumns={"repeat(4, 1fr)"} gap={0.75}>
                        {exactScores.map((score, index) => {
                            const intensity = maxExactScore ? score.probability / maxExactScore : 0
                            const isTop = index === 0
                            return <Box key={score.score} position={"relative"} textAlign={"center"} padding={1}
                                        sx={{
                                            borderRadius: "6px",
                                            background: isTop ? alpha(colors.accent, 0.16) : alpha(colors.accent, intensity * 0.12),
                                            border: `1px solid ${alpha(colors.accent, isTop ? 0.33 : intensity * 0.25)}`,
                                            transition: "transform 200ms",
                                            ":hover": {transform: "scale(1.05)"},
                                        }}>
                                {isTop && <Box position={"absolute"} top={-6} left={"50%"} sx={{
                                    transform: "translateX(-50%)",
                                    paddingX: 0.5,
                                    borderRadius: "999px",
                                    fontSize: "0.5rem",
                                    fontFamily: MONO,
                                    fontWeight: 700,
                                    background: colors.accent,
                                    color: colors.backdrop,
                                }}>★</Box>}
                                <Typography sx={{
                                    fontFamily: MONO,
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    color: isTop ? colors.accent : alpha(colors.foreground, 0.35 + intensity * 0.65),
                                }}>
                                    {score.score}
                                </Typography>
                                <Typography sx={{
                                    fontFamily: MONO,
                                    fontSize: "0.625rem",
                                    color: alpha(colors.muted, 0.5 + intensity * 0.5),
                                }}>
                                    {score.probability}%
                                </Typography>
                            </Box>
                        })}
                    </Box>
                </Sheet>
            </Box>

            {/* Recommended bets + reasoning */}
            <Box display={"grid"} gap={2} gridTemplateColumns={{xs: "1fr", md: "1fr 1.4fr"}}>
                <Sheet sx={panelSx(colors)}>
                    <PanelHeader icon={<CheckCircle2 size={11} color={colors.positive}/>} title={t("panelRecommendedBets")}
                                 color={colors.positive}/>
                    <Stack gap={1}>
                        {recommendedBets.map((bet, index) => <BetChip key={index} {...bet}/>)}
                    </Stack>
                </Sheet>

                <Sheet sx={panelSx(colors)}>
                    <PanelHeader icon={<AlertCircle size={11} color={colors.accent}/>} title={t("panelAiReasoning")} color={colors.accent}/>
                    <Typography level={"body-xs"} sx={{color: colors.bodyText, lineHeight: 1.8}}>
                        {prediction.reasoning?.[language] || prediction.reasoning?.en}
                    </Typography>
                    <Stack direction={"row"} flexWrap={"wrap"} gap={0.75} marginTop={1.5} paddingTop={1.5}
                           sx={{borderTop: `1px solid ${alpha(colors.accent, 0.08)}`}}>
                        <Pill color={colors.muted}>Model run · {formatDateTime(prediction.run_at)}</Pill>
                        {event.updated_at && <Pill color={colors.muted}>Updated · {formatDateTime(event.updated_at)}</Pill>}
                    </Stack>
                </Sheet>
            </Box>
        </>}

        <Typography level={"body-xs"} textAlign={"center"} sx={{fontFamily: MONO, color: colors.muted}}>
            AI predictions are for entertainment only · Gamble responsibly
        </Typography>
    </Stack>
}

export default MatchAnalysis
